import { useCallback, useEffect, useRef, useState } from "react";
import {
    canAccessShopOrSimulator,
    ensurePlayerProfile,
    fetchPlayerProfile,
    updatePlayerSteamId,
} from "@/lib/playerProfile";
import { translate } from "@/i18n/I18nProvider";

/**
 * @param {import('@supabase/supabase-js').User | null | undefined} user
 */
export function usePlayerProfile(user) {
    const userId = user?.id ?? null;
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(Boolean(userId));
    const [savingSteam, setSavingSteam] = useState(false);
    const profileRef = useRef(null);
    profileRef.current = profile;

    const reload = useCallback(async () => {
        if (!userId || !user) {
            setProfile(null);
            setLoading(false);
            return;
        }
        const alreadyLoaded = profileRef.current?.id === userId;
        if (!alreadyLoaded) setLoading(true);

        let p = await fetchPlayerProfile(user);
        if (!p) {
            p = await ensurePlayerProfile(user);
        }
        setProfile(p);
        setLoading(false);
    }, [userId, user]);

    useEffect(() => {
        reload();
    }, [reload]);

    const saveSteamId = useCallback(
        async (raw) => {
            if (!user?.id) return { ok: false, error: translate("auth.error.invalidSession") };
            setSavingSteam(true);
            const result = await updatePlayerSteamId(user.id, raw, user);
            if (result.ok && result.profile) {
                setProfile(result.profile);
            }
            setSavingSteam(false);
            return result;
        },
        [user],
    );

    const access = canAccessShopOrSimulator(user, profile);

    return {
        profile,
        loading,
        savingSteam,
        reload,
        saveSteamId,
        access,
        hasSteamId: access.ok,
        needsLogin: !user?.id,
        needsSteam: Boolean(user?.id && !access.ok && access.reason === "steam"),
    };
}
